import { saveAs } from 'file-saver';
import type { Dispatch, AnyAction } from 'redux';

import type { TaskRecoveryContextType } from 'af-root/context/taskRecoveryContext';
import { toQueryParamsReducer, type ReportTypeViewTableAdditionalFilter } from 'af-root/scenes/Company/FieldReports/WorkOrdersList/ReportTypeViewTable/types';

import * as AddInstanceAPI from 'ab-api/web/fieldReport/addInstance';
import * as AddRepeatableFieldAPI from 'ab-api/web/fieldReport/addRepeatingFields';
import * as AddSegmentAPI from 'ab-api/web/fieldReport/addSegment';
import * as CheckIfBillableWorkExistsAPI from 'ab-api/web/fieldReport/checkIfBillableWorkExists';
import * as DeleteFieldReportAPI from 'ab-api/web/fieldReport/deleteFieldReport';
import * as DeletePDFGenerationProcessAPI from 'ab-api/web/fieldReport/deletePDFGenerationProcess';
import * as DownloadFieldReportTypeAPI from 'ab-api/web/fieldReport/downloadFieldReportType';
import * as UpdateFieldValueAPI from 'ab-api/web/fieldReport/editFieldReportFieldValue';
import * as EditFieldReportTypesAPI from 'ab-api/web/fieldReport/editFieldReportTypes';
import * as FindAllReportTypesForCompanyAPI from 'ab-api/web/fieldReport/findAllReportTypesForCompany';
import * as FindForPreviewAPI from 'ab-api/web/fieldReport/findForPreviewInModal';
import * as FindOrCreateGroupAPI from 'ab-api/web/fieldReport/findOrCreateGroup';
import * as FindWorkSummaryAPI from 'ab-api/web/fieldReport/findWorkSummary';
import * as GeneratePDFAndAttachToWorkOrdersAPI from 'ab-api/web/fieldReport/generatePDFAndAttachToWorkOrders';
import * as GetEquipmentListAPI from 'ab-api/web/fieldReport/getEquipmentList';
import * as RemoveFieldReportSignatureAPI from 'ab-api/web/fieldReport/removeFieldReportSignature';
import * as RemoveSegmentAPI from 'ab-api/web/fieldReport/removeSegment';
import * as ResetJobWorkSummaryAPI from 'ab-api/web/fieldReport/resetJobWorkSummary';
import * as CondensedViewSingleAPI from 'ab-api/web/workOrderReport/condensedViewSingle';
import * as CondensedViewTableAPI from 'ab-api/web/workOrderReport/condensedViewTable';
import * as ReportTypeViewTableAPI from 'ab-api/web/workOrderReport/reportTypeViewTable';
import * as TimeCardUnassignedEntriesAPI from 'ab-api/web/workOrderReport/timeCardUnassignEntriesSidePanel';
import * as TimeCardViewTableAPI from 'ab-api/web/workOrderReport/timeCardViewTable';

import type * as FieldReportSyncVMs from '@acceligentllc/shared/dtos/socket/view/newFieldReport/sync';

import type FieldReportCreateVM from 'ab-viewModels/fieldReport/create.viewModel';
import type NewFieldReportVM from 'ab-viewModels/fieldReport/fieldReport.viewModel';
import type FieldReportBlockFieldImageVM from 'ab-viewModels/fieldReport/fieldReportBlockFieldImage.viewModel';
import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';
import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type ReportListForBulkSendItemVM from 'ab-viewModels/report/listForBulkSendItem.viewModel';
import type SocketConnectionCount from 'ab-viewModels/socketConnectionCount.viewModel';
import type WorkOrderForReportsTableVM from 'ab-viewModels/workOrder/workOrderForReportsTable.viewModel';

import type * as FieldReportRequestModels from 'ab-requestModels/fieldReport/fieldReport.requestModel';
import type { FieldReportBlockFieldUploadRM } from 'ab-requestModels/fieldReport/fieldReportSubmit.requestModel';
import type * as SignatureRequestModel from 'ab-requestModels/signature.requestModel';

import type { LockFieldReportBlockVM } from 'ab-socketModels/viewModels/fieldReport/lockFieldReportBlock.viewModel';

import type { FieldReportTableAdditionalFilters } from 'ab-paramModels/workOrder.params';

import type { SignatureFieldVM } from '@acceligentllc/shared/utils/fieldReport';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import * as FieldReportUtils from 'af-utils/fieldReport.util';
import * as FormUtil from 'af-utils/form.util';
import { http } from 'af-utils/http.util';
import { awaitableTimeout } from 'af-utils/timeout.util';

import type { TimeEntriesFilterType } from 'ab-enums/timeEntries.enum';

import API from 'af-constants/routes/api';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { errorToast, loadingToast, successToast, updateToastContent, warningToast } from '@acceligentllc/storybook';

import * as fieldReportActionCreators from 'af-actions/fieldReport/fieldReport.actionCreators';

// #region create

export function createFieldReport(workOrderId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const data = { workOrderId } as FieldReportRequestModels.CreateRM;
			return await http.post<FieldReportCreateVM>(API.V1.FIELD_REPORT.CREATE_FIELD_REPORT, data);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion

// #region read

export function findById(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			dispatch(fieldReportActionCreators.SET_FIELD_REPORT_NEW(undefined));
			const report = await http.get<NewFieldReportVM>(API.V1.FIELD_REPORT.FIND_BY_ID(id));
			dispatch(fieldReportActionCreators.SET_FIELD_REPORT_NEW(report));
			return report;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

/** TODO: create Report actions and move this there */
export function findAllForReportsTable(
	tableRequestModel: TableQuery,
	startDate: Nullable<Date>,
	endDate: Nullable<Date>,
	additionalFilters: FieldReportTableAdditionalFilters) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			const startDateString = startDate ? TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY) : null;
			const endDateString = endDate ? TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY) : null;

			return await http.get<TableContent<WorkOrderForReportsTableVM>>(
				API.V1.WORK_ORDER.LIST_REPORTS(data, startDateString, endDateString, additionalFilters)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

/** TODO: create Report actions and move this there */
export function findReportsListForBulkSend(workRequestId: number, startDate: Date, endDate: Date) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);

			return await http.get<ReportListForBulkSendItemVM[]>(
				API.V1.JOB.REPORTS_LIST_FOR_BULK_SEND(workRequestId, startDateString, endDateString)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForReportsCondensedTable(
	startDate: Date,
	endDate: Date,
	page: number,
	limit: number,
	filterText: string,
	sortBy: 'workOrderCode' | 'location' | 'dueDate' | 'reviewStatus',
	sortAscending: boolean
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {

			return await http.get<CondensedViewTableAPI.W_WorkOrderReport_CondensedViewTable_VM>(
				CondensedViewTableAPI.URL(
					TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
					TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY),
					page,
					limit,
					filterText,
					sortBy,
					sortAscending
				)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findByIdForReportsCondensedTable(workOrderId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<CondensedViewSingleAPI.W_WorkOrderReport_CondensedViewSingle_VM>(CondensedViewSingleAPI.URL(workOrderId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllReportTypesForCompany() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllReportTypesForCompanyAPI.W_FieldReport_FindAllReportTypesForCompany_VM>(FindAllReportTypesForCompanyAPI.URL());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForReportTypeViewTable(
	startDate: Date,
	endDate: Date,
	page: number,
	limit: number,
	filterText: string,
	sortBy: 'reportTypeName' | 'workOrderCode' | 'companyName' | 'startDate' | 'calculatedReportDisplayStatus' | 'reportTypeStatus' | '',
	sortAscending: boolean,
	filterByReportTypeId: number,
	additionalFilters: ReportTypeViewTableAdditionalFilter[]
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<ReportTypeViewTableAPI.W_ReportType_FindReportTypeViewTable_VM>(
				ReportTypeViewTableAPI.URL(
					TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
					TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY),
					page,
					limit,
					filterText,
					sortBy,
					sortAscending,
					filterByReportTypeId,
					toQueryParamsReducer(additionalFilters)
				)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function fetchListOfUsersWithEntries(tableRequestModel: TableQuery, entriesFilter: TimeEntriesFilterType) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TimeCardViewTableAPI.W_WorkOrderReport_UsersForTimeCardViewTable_VM>(TimeCardViewTableAPI.URL(data, entriesFilter));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function fetchUnassignedEntriesFromAccount(accountId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<TimeCardUnassignedEntriesAPI.W_WorkOrderReport_UnassignedEntries_VM[]>(TimeCardUnassignedEntriesAPI.URL(accountId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findFieldReportTypeForSidePanel(fieldReportId: number, fieldReportTypeId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FieldReportTypePreviewInModalVM>(API.V1.FIELD_REPORT.FIND_FIELD_REPORT_TYPE_FOR_SIDE_PANEL(fieldReportId, fieldReportTypeId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findFieldReportTypeForPreview(fieldReportId: number, fieldReportTypeId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindForPreviewAPI.W_FieldReport_TypePreviewInModal_VM>(FindForPreviewAPI.URL(fieldReportId, fieldReportTypeId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

const _handleGeneratePDFRecoveryWithToast = (
	data: DownloadFieldReportTypeAPI.W_FieldReport_DownloadFieldReportType_VM,
	groupName: string,
	taskRecovery: TaskRecoveryContextType,
	allowedAttempts: number
): boolean => {

	if (!data.resourceErrors?.length) {
		return false;
	}
	taskRecovery.addErrors(groupName, data.resourceErrors);
	const tasksThatCancelTheAction = taskRecovery.getTasksAboveThreshold(groupName, allowedAttempts);

	if (tasksThatCancelTheAction.length) {
		errorToast(
			groupName,
			{
				title: 'Export failed to generate',
				description: tasksThatCancelTheAction.map((error) => error.task).join('; '),
				autoClose: false,
				timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
			}
		);
		return true;
	}
	warningToast(
		groupName,
		{
			title: 'There was an error generating. Retrying...',
			description: data.resourceErrors ? data.resourceErrors.join('; ') : undefined,
			timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
		}
	);

	// Check finalizing actions errors
	if (!data.error?.length) {
		return false;
	}
	taskRecovery.addErrors(groupName, data.error);
	const finalizingTasksThatCancelTheAction = taskRecovery.getTasksAboveThreshold(groupName, allowedAttempts);

	if (finalizingTasksThatCancelTheAction.length) {
		errorToast(
			`${groupName}-error`,
			{
				title: 'Final PDF failed to generate',
				description: finalizingTasksThatCancelTheAction.map((error) => error.task).join('; '),
				autoClose: false,
				timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
			}
		);
		return true;
	}

	warningToast(
		groupName,
		{
			title: 'There was an error generating complete PDF. Retrying...',
			description: data.error ? data.error.join('; ') : undefined,
			timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
		}
	);

	return false;
};

export function attachFieldReportTypePDF(
	fieldReportTypeId: number,
	workOrderIdsToAttach: number[]
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.post<void>(
				GeneratePDFAndAttachToWorkOrdersAPI.URL(fieldReportTypeId),
				{ workOrderIds: workOrderIdsToAttach }
			);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function downloadFieldReportTypePDF(
	groupCode: string,
	filename: string,
	taskRecovery: TaskRecoveryContextType,
	allowedAttempts: number,
	isForCustomer: boolean
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		/** Used to identify toast to update */
		const toastId = `${groupCode}-report_type_export`;
		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);

		loadingToast(toastId, { title: 'Fetching file state...', timestamp: startingTimestamp });

		const action = async () => {
			const data = await http.post<DownloadFieldReportTypeAPI.W_FieldReport_DownloadFieldReportType_VM>(
				DownloadFieldReportTypeAPI.URL(groupCode, isForCustomer)
			);
			if (!data.url) {
				// Message received that we're still generating the report. We will ping in 2 seconds to check again
				// We're jumping through hoops to make sure the caller of this action doesn't have to worry about retrying mechanism
				const newMessage = !!data.merging ? 'Finalizing' : `Generating export. ${data.done ?? 0} / ${data.total} done.`;
				updateToastContent(toastId, { title: newMessage, timestamp: startingTimestamp });

				const shouldStopTask = _handleGeneratePDFRecoveryWithToast(data, groupCode, taskRecovery, allowedAttempts);
				if (shouldStopTask) {
					warningToast(toastId, { title: 'Generating PDF aborted.', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });
					taskRecovery.clearGroupErrors(groupCode);
					await http.delete(DeletePDFGenerationProcessAPI.URL(groupCode));
					return;
				}
				await awaitableTimeout(action, 2000);
			} else {
				updateToastContent(toastId, { title: 'Downloading export...', description: filename, timestamp: startingTimestamp });

				const fetchedFile = await fetch(data.url);
				const blob = await fetchedFile?.blob();
				saveAs(blob, `${filename}.pdf`);

				successToast(toastId, { title: 'Export successfully downloaded', description: filename, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });

				return;
			}
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion

// #region update

export function updateFieldReportBlockCompletedStatus(id: number, fieldReportBlockId: number, completed: boolean) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put(API.V1.FIELD_REPORT.UPDATE_BLOCK_COMPLETED_STATUS(id, fieldReportBlockId), { completed });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findOrCreateGroup(fieldReportTypeIds: number[], isForCustomer: boolean) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<string>(FindOrCreateGroupAPI.URL(), { fieldReportTypeIds, isForCustomer });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editFieldReportTypes(id: number, reportTypes: EditFieldReportTypesAPI.W_FieldReport_EditFieldReportTypes_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put<void>(EditFieldReportTypesAPI.URL(id), reportTypes);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editFieldReportValue(id: number, fieldReportTypeId: number, data: UpdateFieldValueAPI.W_FieldReport_EditFieldReportValue_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.put<void>(UpdateFieldValueAPI.URL(id, fieldReportTypeId), data);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function addInstance(fieldReportId: number, fieldReportTypeId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		// We need retries in case user immediately adds another instance before removing focus from the only filled FR field
		const MAX_RETRIES = 2;
		const RETRY_DELAY_MS = 200;

		const action = async (attempt = 0): Promise<void> => {
			try {
				await http.post<void>(AddInstanceAPI.URL(fieldReportId, fieldReportTypeId));
			} catch (err) {
				const status = err?.response?.status ?? err?.status;
				if (status === 417 && attempt < MAX_RETRIES) {
					await awaitableTimeout(() => action(attempt + 1), RETRY_DELAY_MS);
				}
				throw err;
			}
		};

		return await errorHandler(() => action(0), dispatch, redirectTo);
	};
}

export function addSegment(id: number, fieldReportTypeId: number, instanceIndex: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		// We need retrys in case user immediately adds another segment before removing focus from the only filled FR field
		const MAX_RETRIES = 2;
		const RETRY_DELAY_MS = 200;

		const action = async (attempt = 0): Promise<void> => {
			try {
				await http.post<void>(AddSegmentAPI.URL(id, fieldReportTypeId, instanceIndex));
			} catch (err) {
				const status = err?.response?.status ?? err?.status;
				if (status === 417 && attempt < MAX_RETRIES) {
					await awaitableTimeout(() => action(attempt + 1), RETRY_DELAY_MS);
				}
				throw err;
			}
		};

		return await errorHandler(() => action(0), dispatch, redirectTo);
	};
}

export function addRepeatableFields(id: number, fieldReportBlockId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post<void>(AddRepeatableFieldAPI.URL(id, fieldReportBlockId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function uploadBlockFieldSignature(
	form: SignatureRequestModel.SignatureRequestModel,
	fieldReportId: number
) {
	return async (dispatch: Dispatch<fieldReportActionCreators.FieldReportAction>, { redirectTo }) => {
		const action = async () => {
			return await http.post<SignatureFieldVM>(API.V1.FIELD_REPORT.FIELD_SIGNATURE(fieldReportId), form);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function deleteBlockFieldSignature(form: RemoveFieldReportSignatureAPI.W_FieldReport_RemoveFieldReportSignature_RM) {
	return async (dispatch: Dispatch<fieldReportActionCreators.FieldReportAction>, { redirectTo }) => {
		const action = async () => {
			return await http.delete<void>(RemoveFieldReportSignatureAPI.URL(), form);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function uploadBlockFieldImage(
	form: FieldReportBlockFieldUploadRM,
	fieldReportId: number,
	fieldReportTypeId: number,
	fieldReportBlockId: number,
	fieldReportBlockFieldId: number
) {
	return async (dispatch: Dispatch<fieldReportActionCreators.FieldReportAction>, { redirectTo }) => {
		const action = async () => {
			const options = {
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = FormUtil.getMultipartFormData(form, 'imageUrl');
			return await http.post<FieldReportBlockFieldImageVM>(
				API.V1.FIELD_REPORT.UPLOAD_FIELD_IMAGE(fieldReportId, fieldReportTypeId, fieldReportBlockId, fieldReportBlockFieldId),
				fd,
				options
			);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #region delete

export function deleteFieldReport(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.delete<void>(DeleteFieldReportAPI.URL(id));
			FieldReportUtils.deleteFieldReport(id);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function removeSegment(
	id: number,
	fieldReportTypeId: number,
	instanceIndex: number,
	segmentIndex: number
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		// starts the loading state
		dispatch(fieldReportActionCreators.SET_SEGMENT_DELETING({ instanceIndex, segmentIndex }));

		const action = async () => {
			const body: RemoveSegmentAPI.W_FieldReport_RemoveSegment_RM = { instanceIndex, segmentIndex };
			await http.delete<void>(RemoveSegmentAPI.URL(id, fieldReportTypeId), body);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function removeRepeatableFields(id: number, fieldReportBlockId: string, index: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.delete(API.V1.FIELD_REPORT.REPEATABLE_FIELDS(id, fieldReportBlockId), { index });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findWorkSummaryDetails(fieldReportId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<WorkSummaryDetailVM[]>(API.V1.FIELD_REPORT.FIND_WORK_SUMMARY_DETAILS(fieldReportId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findWorkSummary(fieldReportId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.get<FindWorkSummaryAPI.W_FieldReport_FindWorkSummary_VM>(FindWorkSummaryAPI.URL(fieldReportId));
			return response.workSummaryRows;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function checkIfBillableWorkExists(fieldReportId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.get<CheckIfBillableWorkExistsAPI.W_FieldReport_CheckIfBillableWorkExists_VM>(
				CheckIfBillableWorkExistsAPI.URL(fieldReportId)
			);
			return response.exists;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function resetJobWorkSummary(fieldReportId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.put<void>(ResetJobWorkSummaryAPI.URL(fieldReportId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function fetchEquipmentList(fieldReportId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<GetEquipmentListAPI.W_FieldReport_GetEquipmentList_VM>(GetEquipmentListAPI.URL(fieldReportId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

// #endregion

// #region dispatch only

export function setLocks(locks: Record<string, LockFieldReportBlockVM>) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.SET_FIELD_REPORT_BLOCK_LOCK(locks));
	};
}

export function removeLock(fieldReportBlockId: string) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.REMOVE_FIELD_REPORT_BLOCK_LOCK(fieldReportBlockId));
	};
}

export function syncBlockValues(syncData: FieldReportSyncVMs.SyncBlock) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.SET_FIELD_REPORT_BLOCK_VALUE_NEW(syncData));
	};
}

export function syncAddInstance(syncData: FieldReportSyncVMs.SyncAddInstance) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.ADD_INSTANCE(syncData));
	};
}

export function syncAddSegment(syncData: FieldReportSyncVMs.SyncAddSegment) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.ADD_SEGMENT(syncData));
	};
}

export function syncRemoveInstance(syncData: FieldReportSyncVMs.SyncRemoveInstance) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.REMOVE_INSTANCE(syncData));
	};
}

export function syncRemoveSegment(syncData: FieldReportSyncVMs.SyncRemoveSegment) {
	return (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.REMOVE_SEGMENT(syncData));
	};
}

export function setConnectionCount(connectionCount: SocketConnectionCount) {
	return async (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.SET_CONNECTION_COUNT(connectionCount));
	};
}

export function syncFieldReportTypes(fieldReportTypesForFieldReport: FieldReportSyncVMs.SyncFieldReportTypes) {
	return async (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.SET_FIELD_REPORT_TYPES(fieldReportTypesForFieldReport));
	};
}

export function syncFieldReportBlockCompletedStatus(event: FieldReportSyncVMs.SyncBlockCompletedStatus) {
	return async (dispatch: Dispatch<AnyAction>) => {
		dispatch(fieldReportActionCreators.SET_FIELD_REPORT_BLOCK_COMPLETED(event));
	};
}

// #endregion
